import { Card } from "@mui/material";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import DataTable from "components/Tables/DataTable/DataTable";
import TableSkeleton from "components/TableSkeleton/TableSkeleton";
import {
  GenerateReportMutationVariables,
  ReportType,
  UserPermissions,
  useStartQuickbooksTimeExportMutation,
} from "generated/graphql";
import { useCheckPermissions } from "hooks/authentication/useCheckPermissions";
import useGlobalMessage from "hooks/notifications/useGlobalMessage";
import useGenerateReport from "hooks/organization/reports/useGenerateReport";
import { useTimeEntriesData } from "hooks/timeTracking/useTimeEntriesData";
import dayjs from "dayjs";
import { kebabCase } from "lodash";
import EditTimeEntryModal from "modules/TimeEntries/EditTimeEntryModal";
import QuickbooksTimeExportResultsModal from "modules/TimeEntries/QuickbooksTimeExportResultsModal";
import { useCallback, useEffect, useRef, useState } from "react";
import TimeEntriesTableFilters from "./TimeEntriesTableFilters";
import TimeEntriesGroupHeader from "./TimeEntriesGroupHeader";

export default function TimeEntriesTable() {
  const [selectedEntry, setSelectedEntry] = useState<any | null>(null);
  const [exportAnchorEl, setExportAnchorEl] = useState<Element | null>(null);
  const [requestData, setRequestData] = useState<GenerateReportMutationVariables | null>(null);
  const [quickbooksExportRunId, setQuickbooksExportRunId] = useState<string | null>(null);
  const [quickbooksExportModalOpen, setQuickbooksExportModalOpen] = useState(false);
  const [quickbooksExportStarting, setQuickbooksExportStarting] = useState(false);
  const downloadFileRef = useRef<HTMLAnchorElement | null>(null);

  const { hasPermissions } = useCheckPermissions();
  const { show } = useGlobalMessage();
  const [generateReport, { data: generateReportData }] = useGenerateReport();
  const [startQuickbooksTimeExport, { loading: quickbooksExportStartingMutation }] =
    useStartQuickbooksTimeExportMutation({
      onCompleted: (data) => {
        setQuickbooksExportStarting(false);
        const runId = data?.startQuickbooksTimeExport?.id;
        if (!runId) {
          return;
        }

        setQuickbooksExportRunId(runId);
        setQuickbooksExportModalOpen(true);
        show({
          message: "QuickBooks export queued.",
          options: { variant: "success" },
        });
      },
      onError: (mutationError) => {
        setQuickbooksExportStarting(false);
        show({
          message: mutationError.message,
          options: { variant: "error" },
        });
      },
    });
  const canManageTimeTracking = hasPermissions(UserPermissions.MANAGE_TIME_TRACKING);

  const {
    loading,
    error,
    tableData,
    pagination,
    filtering,
    sorting,
    JobPhaseFilter,
    UserFilter,
    DateFilter,
    ViewModeSwitch,
    groupMode,
    toggleDateGroup,
    toggleUserGroup,
  } = useTimeEntriesData();

  const handleRowClick = useCallback(
    (row: any) => {
      if (!canManageTimeTracking || row.isGroupHeader) {
        return;
      }
      setSelectedEntry(row);
    },
    [canManageTimeTracking]
  );

  const hasDateRange = Boolean(filtering.forDateStart && filtering.forDateEnd);

  useEffect(() => {
    if (!generateReportData?.generateReport || !downloadFileRef.current || !requestData) {
      return;
    }

    downloadFileRef.current.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${encodeURI(
      generateReportData.generateReport
    )}`;
    downloadFileRef.current.download = `bitumio-${kebabCase(requestData.type)}-report.xlsx`;
    downloadFileRef.current.click();
  }, [generateReportData, requestData]);

  const handleOpenExportMenu = useCallback(
    (event: React.MouseEvent<Element>) => {
      if (!hasDateRange) {
        show({
          message: "Please select a Date Range to Export.",
          options: { variant: "error" },
        });
        return;
      }
      setExportAnchorEl(event.currentTarget);
    },
    [hasDateRange, show]
  );

  const handleCloseExportMenu = useCallback(() => {
    setExportAnchorEl(null);
  }, []);

  const handleExcelExport = useCallback(() => {
    if (!hasDateRange) {
      show({
        message: "Please select a Date Range to Export.",
        options: { variant: "error" },
      });
      return;
    }

    const variables: GenerateReportMutationVariables = {
      type: ReportType.TIME_ENTRIES,
      startDate: filtering.forDateStart,
      endDate: filtering.forDateEnd,
    };

    setRequestData(variables);
    generateReport({ variables });
    handleCloseExportMenu();
  }, [
    filtering.forDateEnd,
    filtering.forDateStart,
    generateReport,
    handleCloseExportMenu,
    hasDateRange,
    show,
  ]);

  const handleQuickbooksExport = useCallback(() => {
    if (!hasDateRange) {
      show({
        message: "Please select a Date Range to Export.",
        options: { variant: "error" },
      });
      return;
    }

    setQuickbooksExportModalOpen(true);
    setQuickbooksExportRunId(null);
    setQuickbooksExportStarting(true);

    startQuickbooksTimeExport({
      variables: {
        startDate: dayjs(filtering.forDateStart).format("YYYY-MM-DD"),
        endDate: dayjs(filtering.forDateEnd).format("YYYY-MM-DD"),
        userId: filtering.userID,
        jobPhaseId: filtering.jobPhaseId,
      },
    });
    handleCloseExportMenu();
  }, [
    filtering.forDateEnd,
    filtering.forDateStart,
    filtering.jobPhaseId,
    filtering.userID,
    handleCloseExportMenu,
    hasDateRange,
    show,
    startQuickbooksTimeExport,
  ]);

  return (
    <Card>
      <a ref={downloadFileRef} className="d-none" />
      <TimeEntriesTableFilters
        hasDateRange={hasDateRange}
        exportAnchorEl={exportAnchorEl}
        onOpenExportMenu={handleOpenExportMenu}
        onCloseExportMenu={handleCloseExportMenu}
        onExcelExport={handleExcelExport}
        onQuickbooksExport={handleQuickbooksExport}
        quickbooksExportDisabled={quickbooksExportStarting || quickbooksExportStartingMutation}
        JobPhaseFilter={JobPhaseFilter}
        UserFilter={UserFilter}
        DateFilter={DateFilter}
        ViewModeSwitch={ViewModeSwitch}
      />
      {(loading || error) && (
        <div style={{ padding: "16px" }}>
          {loading && <TableSkeleton numberOfLines={pagination.pageSize} />}
          {error && <MDAlert color="error">{error.message}</MDAlert>}
        </div>
      )}
      <MDBox>
        {!loading && !error && (
          <DataTable
            table={tableData}
            showTotalEntries={false}
            isSorted={groupMode === "date"}
            noEndBorder
            pagination={pagination}
            filtering={filtering}
            sorting={sorting}
            contentPaddingX={3}
            entriesPerPagePosition="bottom"
            onRowClick={handleRowClick}
            isRowClickable={(row: any) => canManageTimeTracking && !row.isGroupHeader}
            groupedRows={{
              isGroupHeader: (row) => Boolean(row.isGroupHeader),
              renderGroupHeader: (row) => (
                <TimeEntriesGroupHeader
                  row={row}
                  onToggleDateGroup={toggleDateGroup}
                  onToggleUserGroup={toggleUserGroup}
                />
              ),
            }}
          />
        )}
      </MDBox>
      {selectedEntry && (
        <EditTimeEntryModal
          open={Boolean(selectedEntry)}
          onClose={() => setSelectedEntry(null)}
          entry={selectedEntry}
        />
      )}
      <QuickbooksTimeExportResultsModal
        open={quickbooksExportModalOpen}
        runId={quickbooksExportRunId}
        isStarting={quickbooksExportStarting || quickbooksExportStartingMutation}
        onClose={() => {
          setQuickbooksExportModalOpen(false);
          setQuickbooksExportStarting(false);
        }}
      />
    </Card>
  );
}
